import React, { useEffect, useState } from "react";
import { ListChecks, PenLine, CalendarCheck, Gift, Play, X } from "lucide-react";
import { api } from "../lib/constants";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { Overline, SectionTitle } from "../components/site/Primitives";
import PlanCard from "../components/PlanCard";
import IndiqueGanhe from "../components/site/IndiqueGanhe";
import SEO from "../components/SEO";

const STEPS = [
  { icon: ListChecks, title: "Escolha seu plano", desc: "Selecione o plano ideal para a sua rotina." },
  { icon: PenLine, title: "Faça sua assinatura", desc: "Assine em poucos cliques com segurança." },
  { icon: CalendarCheck, title: "Agende seus horários", desc: "Marque seus atendimentos pela agenda." },
  { icon: Gift, title: "Aproveite os benefícios", desc: "Brindes, descontos e visual sempre em dia." },
];

function Group({ label, plans }) {
  if (!plans.length) return null;
  return (
    <div className="mt-14">
      <Reveal><Overline className="mb-6">{label}</Overline></Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.06}><PlanCard plan={p} /></Reveal>
        ))}
      </div>
    </div>
  );
}

export default function Clube() {
  const { site } = useSite();
  const [plans, setPlans] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  const principais = plans.filter((p) => p.group === "principais");
  const familia = plans.filter((p) => p.group === "familia");
  const economico = plans.filter((p) => p.group === "economico");

  return (
    <div>
      <SEO title="Clube Barbearia das Estrelas | Assinatura de Barbearia" description="Clube de assinatura de barbearia: corte e barba ilimitados com mensalidade fixa. Planos individuais, família e econômico." />

      <section className="relative overflow-hidden border-b border-white/10 py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/8 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <Reveal><Overline className="mb-4">Clube de assinatura</Overline></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-heading font-700 uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              CLUBE BARBEARIA <span className="text-gold">DAS ESTRELAS</span>
            </h1>
          </Reveal>
          <Reveal delay={0.1}><p className="mt-5 text-zinc-300 text-lg max-w-xl mx-auto">Seu visual sempre em dia pagando uma mensalidade fixa.</p></Reveal>
        </div>
      </section>

      {site?.club_video && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 lg:pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center border border-white/12 rounded-sm bg-[#0b0b0b] p-6 lg:p-10">
            <Reveal>
              <div onClick={() => setShowVideo(true)} data-testid="club-video" className="relative aspect-[4/5] max-h-[520px] overflow-hidden rounded-sm border border-white/10 cursor-pointer group bg-black">
                <img src={site.club_video_poster} alt="Depoimento do Clube das Estrelas" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/25" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 rounded-full bg-gold/90 text-black flex items-center justify-center shadow-xl transition-transform group-hover:scale-110"><Play className="w-7 h-7 ml-1 fill-black" /></span>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div>
                <Overline className="mb-3">Depoimento</Overline>
                <SectionTitle>Quem assina, recomenda</SectionTitle>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  As vantagens de ser assinante da Barbearia das Estrelas na voz de quem vive a experiência. No depoimento, um cliente conta como é fazer parte do clube.
                </p>
                <div className="mt-5 flex items-center gap-3 border border-gold/30 bg-gold/5 rounded-sm px-4 py-3">
                  <Gift className="w-5 h-5 text-gold shrink-0" />
                  <p className="text-sm text-zinc-200">Indique amigos: a cada indicação que vira assinatura, você ganha benefícios.</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-8">
        <Group label="Planos principais" plans={principais} />
        <Group label="Planos para família" plans={familia} />
        <Group label="Plano econômico" plans={economico} />
      </section>

      <section className="bg-[#080808] border-y border-white/10 py-20 lg:py-28 mt-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><Overline className="mb-3">Como funciona o clube</Overline></Reveal>
          <Reveal delay={0.05}><SectionTitle>Simples do início ao fim</SectionTitle></Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {STEPS.map((s, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="border border-white/10 rounded-sm p-6 h-full bg-[#0d0d0d] hover:border-gold/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <s.icon className="w-7 h-7 text-gold" />
                    <span className="font-heading font-700 text-4xl text-white/10">{i + 1}</span>
                  </div>
                  <h3 className="font-heading font-600 uppercase text-white text-lg mt-4">{s.title}</h3>
                  <p className="text-zinc-400 text-sm mt-2 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDIQUE E GANHE */}
      <IndiqueGanhe className="py-20 lg:py-28" />

      {showVideo && site?.club_video && (
        <div className="fixed inset-0 z-[70] bg-black/92 flex items-center justify-center p-4" onClick={() => setShowVideo(false)} data-testid="club-video-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white z-10" onClick={() => setShowVideo(false)} aria-label="Fechar"><X className="w-8 h-8" /></button>
          <video src={site.club_video} controls autoPlay playsInline className="max-h-[88vh] max-w-full rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
